import React from 'react';
import { Box, Spacer, Typography, useTheme } from '@bcp-nextgen-dx-component-factory/accolade-design-system';
import { DisplayCost } from './displayCost';
import { Check } from '@bcp-nextgen-dx-component-factory/accolade-design-system/dist/assets/MBCP/icons';
import { rem } from '@helpers';
import { CostSummarySubscription, CostSummarySubscriptionProps } from './costSummary.subscription';
import { CostSummaryIntegratedSolution, CostSummaryIntegratedSolutionProps } from './costSummary.integratedSolution';
import { SolutionsType } from '@types';
import { useTranslation } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { useServiceResponse } from '@store';
import { PromoCodeStatus } from '@api/izibizi-xp';
export interface SummaryItem {
	description: string;
	cost: number;
}

const flexSettings = {
	justifyContent: 'space-between',
	alignItems: 'center',
};

interface SubscriptionProps {
	summaryItemList: SummaryItem[];
	hasPromotionalCode: boolean;
}

interface IntegratedSolutionProps {
	hasDiscount: boolean;
	solutionId: SolutionsType;
}
interface CostSummaryProps {
	name: string;
	discountPrice: number;
	monthsPromotion: number;
	mainPrice: number;
	footerInformation: string;
}

export declare type CostSummaryCustomProps = WithCostSummaryType<
	SubscriptionProps,
	IntegratedSolutionProps,
	CostSummaryProps
>;
declare type WithCostSummaryType<subscriptionProps, integratedSolutionProps, costSummaryProps> =
	| {
			flow: 'subscription';
			extendsProps: subscriptionProps;
			commonProps: costSummaryProps;
	  }
	| {
			flow: 'integratedSolution';
			extendsProps: integratedSolutionProps;
			commonProps: costSummaryProps;
	  };

export const CostSummary: React.FC<CostSummaryCustomProps> = (props) => {
	const { flow, extendsProps, commonProps } = props;
	const { footerInformation } = commonProps;
	const { palette } = useTheme();
	const t = useTranslation();

	const { retrievePromoCodeDetail } = useServiceResponse();

	const codeStatus = retrievePromoCodeDetail?.promoCodeStatus;
	const isSubCodeValid = codeStatus === PromoCodeStatus.ACTIVE ? true : false;

	let costSummaryProps: CostSummarySubscriptionProps | CostSummaryIntegratedSolutionProps;

	if (flow === 'subscription') {
		costSummaryProps = {
			...commonProps,
			summaryItemList: extendsProps.summaryItemList,
			hasPromotionalCode: extendsProps.hasPromotionalCode,
		} as CostSummarySubscriptionProps;
	} else {
		costSummaryProps = {
			...commonProps,
			hasDiscount: extendsProps.hasDiscount,
			solutionId: extendsProps.solutionId,
		} as CostSummaryIntegratedSolutionProps;
	}

	return (
		<Box
			styles={{
				padding: '24px',
				backgroundColor: palette.global.ui18,
				borderRadius: '8px',
				flexDirection: 'column',
			}}
		>
			{flow === 'subscription' ? (
				<>
					<CostSummarySubscription {...(costSummaryProps as CostSummarySubscriptionProps)} />
					{!extendsProps.hasPromotionalCode && <Spacer vertical={rem(32)} />}
					{extendsProps.hasPromotionalCode && (
						<>
							<Box styles={{ justifyContent: 'space-between', alignItems: 'center' }}>
								<Typography appearance={'body-100-03'}>
									{t('placeholders.costSummary.costAfterPromotionalCode', {
										month: Number(commonProps.monthsPromotion) + 1,
									})}
								</Typography>
								<DisplayCost cost={commonProps.mainPrice} appearance={'main'} />
							</Box>
							<Spacer vertical={rem(15)} />
						</>
					)}
					{/* to change the showcase of the divided Values according to promotion codes CON */}
					{extendsProps.summaryItemList !== undefined &&
						extendsProps.summaryItemList.map(({ description, cost }, index) => {
							return (
								<Box key={`service-${index}`} styles={{ ...flexSettings, marginBottom: '8px' }}>
									<Check fill="#80828F" width={24} height={24} />
									<Box styles={{ marginRight: 'auto', marginLeft: 8 }}>
										<Typography appearance="body-75-01">{description}</Typography>
									</Box>
									{!isSubCodeValid && <DisplayCost cost={cost} appearance={'secondary'} />}
								</Box>
							);
						})}
				</>
			) : (
				<CostSummaryIntegratedSolution {...(costSummaryProps as CostSummaryIntegratedSolutionProps)} />
			)}
			<Box styles={{ marginTop: '8px' }}>
				<Typography appearance="microcopy-75-02" colorsLevel="neutralPrimary">
					{footerInformation}
				</Typography>
			</Box>
		</Box>
	);
};
